import { Container, IDestroyOptions, ObservablePoint } from "pixi.js";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { UpgradeTutorialFirstTooltipComponent } from "./UpgradeTutorialFirstTooltipComponent";
import { UpgradeTutorialSecondTooltipComponent } from "./UpgradeTutorialSecondTooltipComponent";
import { UpgradeTutorialThirdTooltipComponent } from "./UpgradeTutorialThirdTooltipComponent";
import { Hand } from "by/gamefactory/cityversetycoon/scenes/common/components/Hand";
import gsap from "gsap";
import { MyBuildingsTutorialResourcePackage } from "../../resources/MyBuildingsTutorialResourcePackage";
import { SoundsResourcePackageWrapper } from "by/gamefactory/cityversetycoon/scenes/common/resources/SoundsResourcePackageWrapper";
type Timeline = gsap.core.Timeline;

export class UpgradeTutorial extends Container {
    private static readonly ANIMATION_TIME = 0.6;
    private static readonly HAND_DEFAULT_POS_X = 620;
    private static readonly HAND_DEFAULT_POS_Y = 1075;
    private static readonly HAND_DEFAULT_ANGLE = 3;
    private static readonly HAND_START_POS_X = 710;
    private static readonly HAND_START_POS_Y = 1850;
    private static readonly HAND_START_ANGLE = -110;

    private readonly tutorialResources: MyBuildingsTutorialResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly soundResources: SoundsResourcePackageWrapper;

    private firstTooltip: UpgradeTutorialFirstTooltipComponent;
    private secondTooltip: UpgradeTutorialSecondTooltipComponent;
    private thirdTooltip: UpgradeTutorialThirdTooltipComponent;
    private hand: Hand;
    private handScale: ObservablePoint;
    private firstTimeline: Timeline;
    private secondTimeline: Timeline;
    private thirdTimeline: Timeline;

    public constructor(
        tutorialResources: MyBuildingsTutorialResourcePackage,
        commonResources: CommonResourcesPackage,
        soundResources: SoundsResourcePackageWrapper
    ) {
        super();
        this.tutorialResources = tutorialResources;
        this.commonResources = commonResources;
        this.soundResources = soundResources;
        this.build();
        this.createAnimations();
    }

    private build(): void {
        this.firstTooltip = new UpgradeTutorialFirstTooltipComponent(this.tutorialResources);
        this.firstTooltip.visible = false;
        this.firstTooltip.pivot.y = this.firstTooltip.height;
        this.firstTooltip.pivot.x = this.firstTooltip.width * 0.57;
        this.firstTooltip.position.set(50 + this.firstTooltip.width * 0.57, 620 + this.firstTooltip.height);
        this.addChild(this.firstTooltip);
        this.secondTooltip = new UpgradeTutorialSecondTooltipComponent(this.tutorialResources);
        this.secondTooltip.visible = false;
        this.secondTooltip.pivot.x = this.firstTooltip.width * 0.18;
        this.secondTooltip.position.set(97 + this.secondTooltip.width * 0.18, 305);
        this.addChild(this.secondTooltip);
        this.thirdTooltip = new UpgradeTutorialThirdTooltipComponent(this.tutorialResources);
        this.thirdTooltip.visible = false;
        this.thirdTooltip.pivot.y = this.thirdTooltip.height;
        this.thirdTooltip.pivot.x = this.thirdTooltip.width * 0.74;
        this.thirdTooltip.position.set(50 + this.thirdTooltip.width * 0.74, 1170 + this.thirdTooltip.height);
        this.addChild(this.thirdTooltip);
        this.hand = new Hand(this.commonResources);
        this.hand.angle = 3;
        this.hand.width = -416;
        this.hand.height = 460;
        this.hand.visible = false;
        this.addChild(this.hand);
        this.handScale = this.hand.scale.clone();
    }

    private createAnimations(): void {
        const duration = UpgradeTutorial.ANIMATION_TIME;

        this.firstTimeline = new gsap.core.Timeline({ paused: true });
        this.firstTimeline.fromTo(this.firstTooltip.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: duration, ease: "back.out(1)" }, "0");
        this.firstTimeline.fromTo(this.hand,
            {
                x: UpgradeTutorial.HAND_START_POS_X,
                y: UpgradeTutorial.HAND_START_POS_Y,
                angle: UpgradeTutorial.HAND_START_ANGLE
            },
            {
                x: UpgradeTutorial.HAND_DEFAULT_POS_X,
                y: UpgradeTutorial.HAND_DEFAULT_POS_Y,
                angle: UpgradeTutorial.HAND_DEFAULT_ANGLE,
                duration: duration,
            }, "0");
        this.firstTimeline.fromTo(this.hand.scale, { x: 0, y: 0 }, { x: this.handScale.x, y: this.handScale.y, duration: duration }, "0");

        this.secondTimeline = new gsap.core.Timeline({ paused: true });
        this.secondTimeline.fromTo(this.secondTooltip.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: duration, ease: "back.out(1)" }, "0");

        this.thirdTimeline = new gsap.core.Timeline({ paused: true });
        this.thirdTimeline.fromTo(this.thirdTooltip.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: duration, ease: "back.out(1)" }, "0");
    }

    public async showFirstTutorial(): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_info_single_general_in_mp3.play();
        this.soundResources.assets_sounds_hand_movemet_general_mp3.play();
        this.firstTooltip.visible = true;
        this.hand.visible = true;
        await this.firstTimeline.play();
    }

    public async hideFirstTutorial(): Promise<void> {
        await this.firstTimeline.reverse();
        this.firstTooltip.visible = false;
        this.hand.visible = false;
    }

    public async showSecondTutorial(): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_info_double_general_in_mp3.play();
        this.secondTooltip.visible = true;
        await this.secondTimeline.play();
    }

    public async hideSecondTutorial(): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_info_single_general_out_mp3.play();
        await this.secondTimeline.reverse();
        this.secondTooltip.visible = false;
    }

    public async showThirdTutorial(): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_info_double_general_in_mp3.play();
        this.thirdTooltip.visible = true;
        await this.thirdTimeline.play();
    }

    public async hideThirdTutorial(): Promise<void> {
        this.soundResources.assets_sounds_myBuildings_upgrade_info_single_general_out_mp3.play();
        await this.thirdTimeline.reverse();
        this.thirdTooltip.visible = false;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.firstTimeline.kill();
        this.secondTimeline.kill();
        this.thirdTimeline.kill();
        super.destroy(options);
    }
}